import React, { useEffect, useState } from "react";
import api from "../services/api"; // Import your API service

const FixTaxInfoPage = () => {
    const [missingData, setMissingData] = useState({
        cities: [],
        counties: [],
        states: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTaxData = async () => {
            try {
                setLoading(true);

                // Fetch data just like in TaxComparisonPage
                const [taxInfoResponse, cityResponse, countyResponse, stateResponse] = await Promise.all([
                    api.get(`/tax-info/:businessId`), // Main tax info API
                    api.get(`/get-city-data/:businessId`), // City tax info
                    api.get(`/get-county-data/:businessId`), // County tax info
                    api.get(`/get-state-data/:businessId`), // State tax info
                ]);

                const taxInfo = taxInfoResponse.data.taxInfo;

                // Extract required data for comparison
                const cities = taxInfo.taxableCities || [];
                const counties = taxInfo.taxableCounties || [];
                const states = taxInfo.groupedEmployees || {};
                const availableCityData = cityResponse.data || [];
                const availableCountyData = countyResponse.data || [];
                const availableStateData = stateResponse.data || [];

                // Determine missing fields for cities
                const missingCities = cities.filter((city) => {
                    const availableCity = availableCityData.find(
                        (data) =>
                            data.city_name === city.city_name &&
                            data.state_code === city.state_code
                    );
                    return !availableCity || !availableCity.account_number || !availableCity.deposit_frequency;
                });

                // Determine missing fields for counties
                const missingCounties = counties.flatMap((countyData) =>
                    countyData.counties.map((countyKey) => {
                        const [state, county] = countyKey.split("_");
                        const availableCounty = availableCountyData.find(
                            (data) =>
                                data.county_name === county && data.county_state === state
                        );
                        return (
                            (!availableCounty || !availableCounty.account_number) && {
                                county_name: county,
                                state_code: state,
                                account_number: availableCounty?.account_number || null,
                            }
                        );
                    })
                ).filter(Boolean);

                // Determine missing fields for states
                const missingStates = Object.keys(states).map((state) => {
                    const withholdingRequired = states[state].stateWithholding || "N/A";
                    const unemploymentRequired = states[state].stateUnemployment || "N/A";
                    const diNeededRequired = states[state].diNeeded || "N/A";

                    const availableState = availableStateData.find((data) => data.state === state);
                    const withholdingAvailable = availableState?.withholding_account_number || "N/A";
                    const unemploymentAvailable = availableState?.uia_account_number || "N/A";
                    const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
                    const diNeededAvailable = availableState?.di_handling || "N/A";
                    const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

                    return (
                        ((withholdingRequired !== "N/A" && withholdingAvailable === "N/A") ||
                            (unemploymentRequired !== "N/A" &&
                                (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")) ||
                            (diNeededRequired !== "N/A" &&
                                (diNeededAvailable === "N/A" || diEmployerRateAvailable === "N/A"))) && {
                            state_name: state,
                            withholdingRequired,
                            withholdingAvailable,
                            unemploymentRequired,
                            unemploymentAvailable,
                            unemploymentTaxRateAvailable,
                            diNeededRequired,
                            diNeededAvailable,
                            diEmployerRateAvailable,
                        }
                    );
                }).filter(Boolean);

                setMissingData({
                    cities: missingCities,
                    counties: missingCounties,
                    states: missingStates,
                });
            } catch (err) {
                console.error("Error fetching tax data:", err);
                setError("Failed to load tax information.");
            } finally {
                setLoading(false);
            }
        };

        fetchTaxData();
    }, []);

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    const { cities, counties, states } = missingData;

    return (
        <div style={{ padding: "20px" }}>
            <h2>Fix Tax Information</h2>

            {cities.length === 0 && counties.length === 0 && states.length === 0 ? (
                <p style={{ color: "green", fontWeight: "bold" }}>
                    Tax information is up to date.
                </p>
            ) : (
                <>
                    {/* Missing Cities */}
                    {cities.length > 0 && (
                        <div>
                            <h3>City Tax Information</h3>
                            {cities.map((city, index) => (
                                <div
                                    key={index}
                                    style={{
                                        margin: "10px 0",
                                        border: "1px solid #ccc",
                                        padding: "10px",
                                    }}
                                >
                                    <h5>{city.city_name}, {city.state_code}</h5>
                                    {!city.account_number && (
                                        <div>
                                            <label>Account Number:</label>
                                            <input
                                                type="text"
                                                placeholder="Enter Account Number"
                                            />
                                        </div>
                                    )}
                                    {!city.deposit_frequency && (
                                        <div>
                                            <label>Deposit Frequency:</label>
                                            <select>
                                                <option>Select Frequency</option>
                                                <option>Monthly</option>
                                                <option>Quarterly</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Missing Counties */}
                    {counties.length > 0 && (
                        <div>
                            <h3>County Tax Information</h3>
                            {counties.map((county, index) => (
                                <div
                                    key={index}
                                    style={{
                                        margin: "10px 0",
                                        border: "1px solid #ccc",
                                        padding: "10px",
                                    }}
                                >
                                    <h5>{county.county_name}, {county.state_code}</h5>
                                    <div>
                                        <label>County Tax Account Number:</label>
                                        <input
                                            type="text"
                                            placeholder="Enter County Tax Account Number"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Missing States */}
                    {states.length > 0 && (
                        <div>
                            <h3>State Tax Information</h3>
                            {states.map((state, index) => (
                                <div
                                    key={index}
                                    style={{
                                        margin: "10px 0",
                                        border: "1px solid #ccc",
                                        padding: "10px",
                                    }}
                                >
                                    <h5>{state.state_name}</h5>
                                    {state.withholdingRequired !== "N/A" &&
                                        state.withholdingAvailable === "N/A" && (
                                            <div>
                                                <label>
                                                    Withholding Account Number:
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Withholding Account Number"
                                                />
                                            </div>
                                        )}
                                    {state.unemploymentRequired !== "N/A" &&
                                        (state.unemploymentAvailable === "N/A" ||
                                            state.unemploymentTaxRateAvailable === "N/A") && (
                                            <div>
                                                <label>UIA Account Number:</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter UIA Account Number"
                                                />
                                                <label>
                                                    Unemployment Tax Rate:
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Unemployment Tax Rate"
                                                />
                                            </div>
                                        )}
                                    {state.diNeededRequired !== "N/A" &&
                                        (state.diNeededAvailable === "N/A" ||
                                            state.diEmployerRateAvailable === "N/A") && (
                                            <div>
                                                <label>DI Handling:</label>
                                                <select>
                                                    <option>Select Handling</option>
                                                    <option>
                                                        Employer Withdraws for DI Insurance
                                                    </option>
                                                    <option>
                                                        State Handles DI Insurance
                                                    </option>
                                                </select>
                                                <label>Employer Rate (%):</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Employer Rate"
                                                />
                                            </div>
                                        )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FixTaxInfoPage;
